import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit, 
  Trash2, 
  Building2, 
  Users, 
  Phone,
  Mail,
  Globe,
  Calendar,
  TrendingUp,
  BarChart,
  PhoneCall,
  Send,
  Eye,
  MousePointer,
  Repeat,
  ChevronDown,
  ChevronUp,
  Activity,
  Target,
  Zap,
  Sparkles
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { Badge } from '@/components/common/Badge';
import { useToast } from '@/components/common/Toast';
import { PageHeader } from '@/components/common/PageHeader';
import { Pagination } from '@/components/common/Pagination';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/common/Tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/common/Dialog';
import * as accountsService from '@/services/api/accountsService';
import * as contactsService from '@/services/api/contactsService';
import type { Account, AccountTimelineEvent, AccountTimelineResponse } from '@/services/api/accountsService';
import type { Contact } from '@/services/api/contactsService';
import SendMagnetModal from '@/features/communication/SendMagnetModal';
import AccountEnrichmentPanel from './AccountEnrichmentPanel';
import apiClient from '@/services/api/apiClient';

// Premium color mappings matching AccountsPage
const tierColors = {
  1: 'tier1',     // Gold gradient
  2: 'tier2',     // Purple gradient
  3: 'tier3',     // Gray gradient
} as const;

const statusColors = {
  ACTIVE: 'active',           // Emerald gradient
  INACTIVE: 'inactive',       // Muted gray
  MQL_READY: 'mqlReady',     // Pink gradient with pulse
  MQL_SUBMITTED: 'mqlSubmitted', // Blue gradient
} as const;

const eventIcons = {
  CALL: PhoneCall,
  MAGNET_DELIVERY: Send,
  ENGAGEMENT: Eye,
};

const eventSubTypeIcons = {
  OPEN: Eye,
  CLICK: MousePointer,
  REVISIT: Repeat,
};

interface ExpandedMetadata {
  [key: string]: boolean;
}

const CONFIDENCE_LABELS: Record<string, string> = {
  CONTACT_SELF_ASSESSED: 'Self Assessed',
  VERIFIED: 'Verified',
  AI_INFERRED: 'AI Inferred',
  MANUAL: 'Manual',
};
const STAGE_LABELS: Record<string, string> = {
  NOT_STARTED: 'Not started',
  AWARENESS: 'Awareness',
  CONSIDERATION: 'Consideration',
  DECISION: 'Decision',
  POST_SALE: 'Post-sale',
};
export default function AccountDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  
  const [account, setAccount] = useState<Account | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [timeline, setTimeline] = useState<AccountTimelineResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [magnetContact, setMagnetContact] = useState<Contact | null>(null);
  const [currentTimelinePage, setCurrentTimelinePage] = useState(1);
  const [expandedMetadata, setExpandedMetadata] = useState<ExpandedMetadata>({});
  const [journeyPickerOpen, setJourneyPickerOpen] = useState(false);
  const [anchorContactId, setAnchorContactId] = useState('');
  const [creatingJourney, setCreatingJourney] = useState(false);

  // Create an ABM journey for the chosen anchor contact (one journey per account+contact).
  const createJourneyForContact = async () => {
    if (!anchorContactId || !id) return;
    setCreatingJourney(true);
    try {
      // If this contact already has a journey, just open it (don't duplicate).
      let existing: any = null;
      try { const r = await apiClient.get(`/api/abm-journeys/contact/${anchorContactId}`); existing = r.data; } catch { /* none */ }
      if (existing && existing.id) {
        addToast({ type: 'info', title: 'Journey exists', description: 'Opening the existing journey for this contact.' });
        navigate(`/abm-journeys/${anchorContactId}`);
        return;
      }
      await apiClient.post('/api/abm-journeys', {
        accountId: id, contactId: anchorContactId,
        currentStage: 1, completedStages: [], stageData: {},
      });
      addToast({ type: 'success', title: 'ABM journey created', description: 'Journey started at stage 1.' });
      navigate(`/abm-journeys/${anchorContactId}`);
    } catch (e: any) {
      addToast({ type: 'error', title: 'Could not create journey', description: e?.response?.data?.error || e?.message || 'Unknown error' });
    } finally {
      setCreatingJourney(false);
      setJourneyPickerOpen(false);
    }
  };

  useEffect(() => {
    if (!id) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch account details
        const accountData = await accountsService.getAccount(id);
        setAccount(accountData);
        
        // Fetch contacts for this account
        const contactsData = await contactsService.getContacts({
          account_id: id,
        });
        setContacts(contactsData.data);
        
      } catch (error: any) {
        console.error('Error fetching account:', error);
        addToast({
          type: 'error',
          title: 'Error',
          description: error.response?.data?.error || 'Failed to load account details',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Fetch timeline when tab is activated
  useEffect(() => {
    if (activeTab === 'timeline' && id && !timeline) {
      fetchTimeline();
    }
  }, [activeTab, id]);

  // Fetch timeline on page change
  useEffect(() => {
    if (activeTab === 'timeline' && id) {
      fetchTimeline();
    }
  }, [currentTimelinePage]);

  const fetchTimeline = async () => {
    if (!id) return;
    
    try {
      const timelineData = await accountsService.getAccountTimeline(id, {
        page: currentTimelinePage,
        limit: 20,
      });
      setTimeline(timelineData);
    } catch (error: any) {
      console.error('Error fetching timeline:', error);
      addToast({
        type: 'error',
        title: 'Error',
        description: 'Failed to load timeline data',
      });
    }
  };

  const handleDelete = async () => {
    if (!account) return;

    try {
      await accountsService.deleteAccount(account.id);
      
      addToast({
        type: 'success',
        title: 'Account Deleted',
        description: `${account.name} has been deleted successfully.`,
      });
      
      navigate('/accounts');
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Error',
        description: error.response?.data?.error || 'Failed to delete account',
      });
    }
  };

  const toggleMetadata = (eventId: string) => {
    setExpandedMetadata(prev => ({
      ...prev,
      [eventId]: !prev[eventId]
    }));
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return {
        date: 'Invalid Date',
        time: '--:--'
      };
    }
    
    return {
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const getEventIcon = (event: AccountTimelineEvent) => {
    if (event.type === 'ENGAGEMENT' && event.subType) {
      const IconComponent = eventSubTypeIcons[event.subType as keyof typeof eventSubTypeIcons];
      return IconComponent || Eye;
    }
    return eventIcons[event.type as keyof typeof eventIcons] || Activity;
  };

  const getEventColor = (color: string) => {
    const colorMap: Record<string, string> = {
      green: 'text-green-600 bg-green-50',
      blue: 'text-blue-600 bg-blue-50',
      purple: 'text-purple-600 bg-purple-50',
      orange: 'text-orange-600 bg-orange-50',
    };
    return colorMap[color] || 'text-gray-600 bg-gray-50';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p className="text-muted-foreground">Loading account details...</p>
        </div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="text-center py-12">
        <Building2 className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
        <h2 className="text-2xl font-bold mb-2">Account Not Found</h2>
        <p className="text-muted-foreground mb-4">
          The account you're looking for doesn't exist or has been deleted.
        </p>
        <Button onClick={() => navigate('/accounts')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Accounts
        </Button>
      </div>
    );
  }

  const isPartner = (account.accountType || 'BUYER').toUpperCase() === 'PARTNER';

  return (
    <div className="space-y-6">
      {/* Create ABM Journey — pick anchor contact */}
      <Dialog open={journeyPickerOpen} onOpenChange={setJourneyPickerOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create ABM Journey</DialogTitle>
            <DialogDescription>
              Choose the contact this journey will be anchored to. Engagement is tracked per contact.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="text-sm font-medium block mb-1.5">Anchor contact</label>
            <select
              value={anchorContactId}
              onChange={e => setAnchorContactId(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm"
            >
              {contacts.map(c => (
                <option key={c.id} value={c.id}>
                  {c.firstName} {c.lastName}{c.title ? ` — ${c.title}` : ''}
                </option>
              ))}
            </select>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={creatingJourney}>Cancel</Button>
            </DialogClose>
            <Button onClick={createJourneyForContact} disabled={!anchorContactId || creatingJourney}>
              {creatingJourney ? 'Creating…' : 'Create Journey'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <PageHeader
        onBack={() => navigate('/accounts')}
        title={account.name}
        description={`${account.industryVertical || 'No industry specified'}${account.region ? ` • ${account.region}` : ''}`}
        actions={
          <>
            <Button
              onClick={() => { setAnchorContactId(contacts[0]?.id || ''); setJourneyPickerOpen(true); }}
              disabled={contacts.length === 0}
              title={contacts.length === 0 ? 'Add a contact first' : 'Create an ABM journey for a contact'}
            >
              <Target className="h-4 w-4 mr-2" />
              Create ABM Journey
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/accounts/${account.id}/edit`)}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit Account
            </Button>
            <Button
              variant="destructive"
              onClick={() => setDeleteDialogOpen(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </>
        }
      />
      {/* Status / tier badges */}
      <div className="flex items-center gap-2 -mt-2">
        <Badge variant={statusColors[account.status] || 'default'}>{account.status.replace('_', ' ')}</Badge>
        {account.abmTier && (
          <Badge variant={tierColors[account.abmTier as keyof typeof tierColors] || 'default'}>
            Tier {account.abmTier}
          </Badge>
        )}
      </div>

      {/* Enhanced Key Metrics with Premium Design */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* ABM Tier Card - Gold Theme */}
        <Card className="border-l-4 border-l-amber-500 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              ABM Tier
            </CardTitle>
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <Target className="h-4 w-4 text-white" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
              {account.abmTier ? `Tier ${account.abmTier}` : 'Not Set'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {isPartner ? (account.partnerTerritory || 'No territory set') : (account.maturityBand || 'No maturity band')}
            </p>
          </CardContent>
        </Card>

        {/* Maturity / Partner-Type Card - Purple Theme */}
        <Card className="border-l-4 border-l-violet-500 bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/20 dark:to-purple-950/20">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              {isPartner ? 'Partner Type' : 'Maturity Level'}
            </CardTitle>
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
              <TrendingUp className="h-4 w-4 text-white" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
              {isPartner
                ? (account.partnerType ? String(account.partnerType).replace(/_/g, ' ') : 'Partner')
                : (account.maturityBand || 'N/A')}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {isPartner
                ? (account.partnerDealModel ? String(account.partnerDealModel).replace(/_/g, ' ') : 'Channel partner')
                : (CONFIDENCE_LABELS[account.maturityConfidence] || account.maturityConfidence || 'Not set')}
            </p>
          </CardContent>
        </Card>

        {/* Contacts Card - Emerald Theme */}
        <Card className="border-l-4 border-l-emerald-500 bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/20 dark:to-green-950/20">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              Contacts
            </CardTitle>
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center">
              <Users className="h-4 w-4 text-white" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">
              {contacts.length}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {contacts.filter(c => c.is_decision_maker || /vp |cto|coo|ceo|cfo|director|chief|president/i.test(c.title || '')).length} decision makers
            </p>
          </CardContent>
        </Card>

        {/* Primary Pain / Market-Focus Card - Cyan Theme */}
        <Card className="border-l-4 border-l-cyan-500 bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-950/20 dark:to-blue-950/20">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              {isPartner ? 'Market Focus' : 'Primary Pain'}
            </CardTitle>
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
              <Zap className="h-4 w-4 text-white" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
              {isPartner ? (account.partnerMarketFocus || account.industryVertical || 'N/A') : (account.primaryPainAxis || 'N/A')}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {isPartner
                ? (account.partnerDeliveryCapacity ? `${account.partnerDeliveryCapacity} capacity` : '—')
                : (STAGE_LABELS[account.automationStage] || account.automationStage || '—')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabbed Interface */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="timeline">
            <Activity className="h-4 w-4 mr-2" />
            Activity Timeline
          </TabsTrigger>
          <TabsTrigger value="enrichment">
            <Sparkles className="h-4 w-4 mr-2" />
            AI Enrichment
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Account Information - Comprehensive 15 Fields */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Account Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Basic Information */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Basic Information
                  </h3>
                  <div className="grid gap-3">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Account Name</label>
                      <p className="text-lg font-semibold mt-1">{account.name}</p>
                    </div>
                    {account.domain && (
                      <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                          <Globe className="h-3 w-3" />
                          Domain
                        </label>
                        <a 
                          href={`https://${account.domain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-base text-primary hover:underline mt-1 block font-medium"
                        >
                          {account.domain}
                        </a>
                      </div>
                    )}
                    <div>
                      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</label>
                      <div className="mt-2">
                        <Badge variant={statusColors[account.status] || 'default'}>
                          {account.status.replace('_', ' ')}
                        </Badge>
                      </div>
                    </div>
                    {account.additionalNotes && (
                      <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Additional Notes</label>
                        <p className="text-base font-medium mt-1 whitespace-pre-wrap">{account.additionalNotes}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Industry & Location */}
                <div className="space-y-3 pt-3 border-t">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    Industry & Location
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {account.industryVertical && (
                      <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Industry</label>
                        <p className="text-base font-medium mt-1">{account.industryVertical}</p>
                      </div>
                    )}
                    {account.subVertical && (
                      <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Sub-Vertical</label>
                        <p className="text-base font-medium mt-1">{account.subVertical}</p>
                      </div>
                    )}
                    {account.region && (
                      <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{isPartner ? 'Territory' : 'Region'}</label>
                        <p className="text-base font-medium mt-1">{isPartner ? (account.partnerTerritory || account.region) : account.region}</p>
                      </div>
                    )}
                    {!isPartner && account.plantType && (
                      <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Plant Type</label>
                        <p className="text-base font-medium mt-1">{account.plantType}</p>
                      </div>
                    )}
                    {isPartner && account.partnerMarketFocus && (
                      <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Market Focus</label>
                        <p className="text-base font-medium mt-1">{account.partnerMarketFocus}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* ABM Configuration */}
                <div className="space-y-3 pt-3 border-t">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                    <Target className="h-4 w-4" />
                    ABM Configuration
                  </h3>
                  <div className="grid grid-cols-3 gap-3">
                    {account.abmTier && (
                      <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">ABM Tier</label>
                        <div className="mt-2">
                          <Badge variant={tierColors[account.abmTier as keyof typeof tierColors] || 'default'}>
                            Tier {account.abmTier}
                          </Badge>
                        </div>
                      </div>
                    )}
                    {!isPartner && account.maturityBand && (
                      <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Maturity Band</label>
                        <p className="text-lg font-bold mt-1">{account.maturityBand}</p>
                      </div>
                    )}
                    {!isPartner && account.maturityConfidence && (
                      <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Confidence</label>
                        <p className="text-base font-medium mt-1">{CONFIDENCE_LABELS[account.maturityConfidence] || account.maturityConfidence}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Partner Profile — PARTNER accounts only */}
                {isPartner && (
                  <div className="space-y-3 pt-3 border-t">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Partner Profile
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      {account.partnerType && (
                        <div>
                          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Partner Type</label>
                          <p className="text-base font-semibold mt-1">{String(account.partnerType).replace(/_/g, ' ')}</p>
                        </div>
                      )}
                      {account.partnerTerritory && (
                        <div>
                          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Territory</label>
                          <p className="text-base font-medium mt-1">{account.partnerTerritory}</p>
                        </div>
                      )}
                      {account.partnerMarketFocus && (
                        <div>
                          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Market / Vertical Focus</label>
                          <p className="text-base font-medium mt-1">{account.partnerMarketFocus}</p>
                        </div>
                      )}
                      {account.partnerDeliveryCapacity && (
                        <div>
                          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Delivery Capacity</label>
                          <p className="text-base font-medium mt-1 capitalize">{account.partnerDeliveryCapacity}</p>
                        </div>
                      )}
                      {account.partnerDealModel && (
                        <div>
                          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Deal Model</label>
                          <p className="text-base font-medium mt-1">{String(account.partnerDealModel).replace(/_/g, ' ')}</p>
                        </div>
                      )}
                      {account.partnerPortfolio && (
                        <div className="col-span-2">
                          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Current Portfolio</label>
                          <p className="text-base font-medium mt-1">{account.partnerPortfolio}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Technical Profile — buyer (MES) only */}
                {!isPartner && (
                <div className="space-y-3 pt-3 border-t">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                    <Zap className="h-4 w-4" />
                    Technical Profile
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {account.primaryPainAxis && (
                      <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Primary Pain Axis</label>
                        <p className="text-base font-semibold mt-1">{account.primaryPainAxis}</p>
                      </div>
                    )}
                    {account.painDescription && (
                      <div className="col-span-2">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Pain Description</label>
                        <p className="text-base font-medium mt-1 whitespace-pre-wrap">{account.painDescription}</p>
                      </div>
                    )}
                    {account.dataReadinessLevel && (
                      <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Data Readiness</label>
                        <p className="text-base font-medium mt-1">{account.dataReadinessLevel}</p>
                      </div>
                    )}
                    {account.automationStage && (
                      <div className="col-span-2">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Automation Stage</label>
                        <p className="text-base font-medium mt-1">{STAGE_LABELS[account.automationStage] || account.automationStage}</p>
                      </div>
                    )}
                    {account.architectureTags && account.architectureTags.length > 0 && (
                      <div className="col-span-2">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Architecture Tags</label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {account.architectureTags.map((tag, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                )}

                {/* Assignment & Metadata */}
                <div className="space-y-3 pt-3 border-t">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Assignment</h3>
                  <div className="grid gap-3">
                    {account.assignedMda && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Assigned MDA</label>
                        <p className="text-base mt-1">{account.assignedMda.name}</p>
                        <p className="text-sm text-muted-foreground">{account.assignedMda.email}</p>
                      </div>
                    )}
                    <div>
                      <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        Created
                      </label>
                      <p className="text-base mt-1">
                        {new Date(account.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        Last Updated
                      </label>
                      <p className="text-base mt-1">
                        {new Date(account.updatedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contacts List */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Contacts ({contacts.length})
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(`/contacts/new?account_id=${account.id}`)}
                  >
                    Add Contact
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {contacts.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No contacts yet</p>
                    <Button
                      size="sm"
                      variant="link"
                      onClick={() => navigate(`/contacts/new?account_id=${account.id}`)}
                    >
                      Add first contact
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {contacts.map((contact) => (
                      <div
                        key={contact.id}
                        className="flex items-start justify-between p-3 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/contacts/${contact.id}`)}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">
                              {contact.firstName || contact.first_name} {contact.lastName || contact.last_name}
                            </p>
                            {(contact.is_decision_maker || /vp |cto|coo|ceo|cfo|director|chief|president/i.test(contact.title || '')) && (
                              <Badge variant="default" className="text-xs">
                                Decision Maker
                              </Badge>
                            )}
                          </div>
                          {contact.title && (
                            <p className="text-sm text-muted-foreground">{contact.title}</p>
                          )}
                          <div className="flex items-center gap-3 mt-1">
                            {contact.email && (
                              <a
                                href={`mailto:${contact.email}`}
                                className="text-xs text-primary hover:underline flex items-center gap-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Mail className="h-3 w-3" />
                                {contact.email}
                              </a>
                            )}
                            {contact.phone && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {contact.phone}
                              </span>
                            )}
                          </div>
                        </div>
                        {contact.email && (
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Send Magnet"
                            onClick={(e) => { e.stopPropagation(); setMagnetContact(contact); }}
                          >
                            <Send className="h-4 w-4 text-primary" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* AI Enrichment Tab */}
        <TabsContent value="enrichment">
          {id && (
            <AccountEnrichmentPanel
              accountId={id}
              onPromoted={async () => {
                try {
                  const fresh = await accountsService.getAccount(id);
                  setAccount(fresh);
                } catch { /* ignore */ }
              }}
            />
          )}
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline">
          <Card>
            <CardHeader>
              <CardTitle>Account Activity Timeline</CardTitle>
              <p className="text-sm text-muted-foreground">
                Complete history of interactions across all contacts in {account.name}
              </p>
            </CardHeader>
            <CardContent>
              {!timeline ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
                    <p className="text-muted-foreground">Loading timeline...</p>
                  </div>
                </div>
              ) : timeline.data.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No interaction history yet</p>
                  <p className="text-sm mt-1">Interactions will appear here as they occur</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/30 rounded-lg">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-primary">{timeline.summary.totalCalls}</div>
                      <div className="text-xs text-muted-foreground">Total Calls</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-primary">{timeline.summary.totalDeliveries}</div>
                      <div className="text-xs text-muted-foreground">Content Delivered</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-primary">{timeline.summary.totalEngagements}</div>
                      <div className="text-xs text-muted-foreground">Engagements</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-primary">{timeline.summary.totalContacts}</div>
                      <div className="text-xs text-muted-foreground">Active Contacts</div>
                    </div>
                  </div>

                  {/* Engagement Breakdown by Contact */}
                  {timeline.summary.contactEngagement && Object.keys(timeline.summary.contactEngagement).length > 0 && (
                    <Card className="bg-muted/20">
                      <CardHeader>
                        <CardTitle className="text-lg">Engagement by Contact</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {Object.entries(timeline.summary.contactEngagement).map(([contactId, engagement]) => (
                            <div key={contactId} className="flex items-center justify-between p-3 rounded-lg border bg-background hover:bg-muted/50 transition-colors">
                              <div className="flex-1">
                                <p className="font-medium">{engagement.name}</p>
                                <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                                  <span className="flex items-center gap-1">
                                    <PhoneCall className="h-3 w-3" />
                                    {engagement.calls} calls
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Send className="h-3 w-3" />
                                    {engagement.deliveries} delivered
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Eye className="h-3 w-3" />
                                    {engagement.engagements} engaged
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Timeline Events */}
                  <div className="relative space-y-6">
                    {/* Vertical Line */}
                    <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border"></div>
                    
                    {timeline.data.map((event) => {
                      const EventIcon = getEventIcon(event);
                      const { date, time } = formatTimestamp(event.timestamp);
                      const isExpanded = expandedMetadata[event.id];
                      
                      return (
                        <div key={event.id} className="relative pl-16">
                          {/* Icon */}
                          <div className={`absolute left-0 w-12 h-12 rounded-full flex items-center justify-center ${getEventColor(event.color)}`}>
                            <EventIcon className="h-5 w-5" />
                          </div>
                          
                          {/* Content */}
                          <div className="bg-muted/30 rounded-lg p-4 hover:bg-muted/50 transition-colors">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <h4 className="font-medium">{event.title}</h4>
                                <p className="text-sm text-muted-foreground">{event.description}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge variant="outline" className="text-xs">
                                    {event.contactName}
                                  </Badge>
                                  <span className="text-xs text-muted-foreground">•</span>
                                  <span className="text-xs text-muted-foreground">{event.type.replace('_', ' ')}</span>
                                </div>
                              </div>
                              <div className="text-right text-sm text-muted-foreground ml-4">
                                <div>{date}</div>
                                <div>{time}</div>
                              </div>
                            </div>
                            
                            {/* Metadata Toggle */}
                            {event.metadata && Object.keys(event.metadata).length > 0 && (
                              <div className="mt-3">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => toggleMetadata(event.id)}
                                  className="text-xs"
                                >
                                  {isExpanded ? (
                                    <>
                                      <ChevronUp className="h-3 w-3 mr-1" />
                                      Hide Details
                                    </>
                                  ) : (
                                    <>
                                      <ChevronDown className="h-3 w-3 mr-1" />
                                      Show Details
                                    </>
                                  )}
                                </Button>
                                
                                {isExpanded && (
                                  <div className="mt-2 p-3 bg-background rounded border text-sm space-y-1">
                                    {Object.entries(event.metadata).map(([key, value]) => (
                                      <div key={key} className="flex gap-2">
                                        <span className="font-medium text-muted-foreground capitalize">
                                          {key.replace(/([A-Z])/g, ' $1').trim()}:
                                        </span>
                                        <span className="text-foreground">
                                          {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pagination */}
                  {timeline.pagination.totalPages > 1 && (
                    <div className="mt-6">
                      <Pagination
                        currentPage={timeline.pagination.page}
                        totalPages={timeline.pagination.totalPages}
                        pageSize={timeline.pagination.limit}
                        totalItems={timeline.pagination.total}
                        onPageChange={setCurrentTimelinePage}
                        onPageSizeChange={() => {}}
                      />
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Send Magnet Modal */}
      <SendMagnetModal
        open={!!magnetContact}
        onClose={() => setMagnetContact(null)}
        contactId={magnetContact?.id || ''}
        contactName={magnetContact ? `${magnetContact.firstName || (magnetContact as any).first_name || ''} ${magnetContact.lastName || (magnetContact as any).last_name || ''}`.trim() : ''}
        contactEmail={magnetContact?.email}
        currentStage={magnetContact?.currentStage}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogClose onClose={() => setDeleteDialogOpen(false)} />
          <DialogHeader>
            <DialogTitle>Delete Account</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{' '}
              <span className="font-semibold">{account.name}</span>?
              This will also affect {contacts.length} contact(s) associated with this account.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
